/* eslint-disable react-hooks/exhaustive-deps */
import { Box, TextField, Typography, makeStyles, withStyles } from "@material-ui/core";
import { Autocomplete } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppToolbar, DatePickerWrapper, DatePickerComponent, MIN_DATE, MAX_DATE } from "../components/AppToolbar";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { SelectComponent } from "../components/SelectComponent";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import useWindowSize from "../hooks/useWindowSize";
import { Months } from "../utils/OpsDateUtils";
import useTableHeight from "../hooks/useTableHeight";
import { useLocation } from "react-router-dom";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { Routes } from "../types/Routes";
import WorkLogService from "../services/WorkLogService";
import UnifiedReportDTO from "../models/UnifiedReportDTO";
import { ColorUtils } from "../utils/ColorUtils";
import moment from "moment";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import UserService from "../services/UserService";
import { traceo } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";

const currentMonth = new Date().toLocaleDateString("en-US", { month: "long" });
const currentYear = new Date().getFullYear().toString();

const YellowTextTypography = withStyles({
  root: {
    color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING,
  },
})(Typography);

export default function UnifiedReports() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const searchParams = new URLSearchParams(useLocation().search);
  const { updateUrl } = useUpdateUrl(Routes.UNIFIED_REPORTS, searchParams);

  const [isOpen, setIsOpen] = useState(false);

  const [users, setUsers] = useState<AutoCompleteUIDTO[]>([]);
  const [selectedUser, setSelectedUser] = useState<AutoCompleteUIDTO | null>(null);
  const [unifiedReports, setUnifiedReports] = useState<UnifiedReportDTO[]>([])

  const totalHours = unifiedReports.reduce((acc, obj) => {return acc + obj.internalHours}, 0)

  const nameFilter = searchParams.get("name");
  const monthFilter = searchParams.get("month");
  const yearFilter = searchParams.get("year");

  useEffect(() => {
    monthFilter || searchParams.set("month", currentMonth);
    yearFilter || searchParams.set("year", currentYear);
    updateUrl();
    UserService.findUserNames()
      .then((response) => {
        setUsers(response.getContentOrThrowError());
      })
      .catch((error) => traceo("Error getting user names.", error, null));
  }, []);

  useEffect(() => {
    if (nameFilter && users.length) {
      const user = users.find((user) => user.label === nameFilter);
      if (user) {
        setSelectedUser(user);
      }
    }
  }, [users]);

  const renderUserNameField = (
    <Autocomplete
      options={users}
      getOptionLabel={(option) => option.label}
      value={selectedUser}
      renderInput={(params) => {
        return <TextField {...params} placeholder={t("Select User")} variant="outlined" value={selectedUser?.label} />;
      }}
      onInputChange={(event, input) => {
        searchParams.set("name", input);
        updateUrl();
      }}
      className={classes.inputStyles}
      autoHighlight
      onChange={(event, value) => setSelectedUser(value as AutoCompleteUIDTO)}
    />
  );

  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        {renderUserNameField}
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {renderUserNameField}
        <SelectComponent
          label={"Month"}
          value={monthFilter || currentMonth}
          setValue={(value) => {
            searchParams.set("month", value);
            updateUrl();
          }}
          options={Object.values(Months).slice(1)}
          customStyle={{ margin: "0 0.5em 0 1.5em" }}
          size="small"
        />
        <DatePickerWrapper>
          <DatePickerComponent
            views="year"
            label="Year"
            value={new Date(+(yearFilter || new Date().getFullYear()), 0, 1)}
            inputFormat="yyyy"
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            onChange={(selectedDate) => {
              searchParams.set("year", selectedDate!.getFullYear().toString());
              updateUrl();
            }}
          />
        </DatePickerWrapper>
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = (
    <>
      <SelectComponent
        label={"Month"}
        value={monthFilter || currentMonth}
        setValue={(value) => {
          searchParams.set("month", value);
          updateUrl();
        }}
        options={Object.values(Months).slice(1)}
        customStyle={{ margin: "0 0.5em 0 1.5em" }}
        size="small"
      />
      <div className={classes.datePicker}>
        <DatePickerWrapper>
          <DatePickerComponent
            views="year"
            label="Year"
            value={new Date(+(yearFilter || new Date().getFullYear()), 0, 1)}
            inputFormat="yyyy"
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            onChange={(selectedDate) => {
              searchParams.set("year", selectedDate!.getFullYear().toString());
              updateUrl();
            }}
          />
        </DatePickerWrapper>
      </div>
    </>
  );

  const getHeader = () => {
    return <AppToolbar title={t("Unified Reports")}>
      <Box justifyContent={{ xs:'flex-end', md:'space-between' }}  flexDirection={{ xs: 'column-reverse', md:'row' }} style={{ display:'flex', width:'100%', alignItems:'center'}}>
        {renderFilters()}
        {unifiedReports.length === 0 
          ? "" 
          : <YellowTextTypography variant="h6" style={{fontSize:'1rem', paddingRight:'0.5em' }}>
            {`Total Hours: ${totalHours}`}
          </YellowTextTypography>}
      </Box>
    </AppToolbar>;
  };

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={getHeader()}
      bottomSection={
        <Box style={{ boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px" }}>
          {useMemo(
            () => (
              <TableBasedCrud<UnifiedReportDTO>
                maxHeight={calculateMaxHeight(false)}
                stickyHeader
                elementName={t("Assignation")}
                onDataFetch={(pageNumber: number, count: number) =>
                  WorkLogService.getUnifiedReports({
                    userExternalId: selectedUser?.externalId || "",
                    month: Object.keys(Months).indexOf((monthFilter || currentMonth).toUpperCase()),
                    year: +(yearFilter || currentYear),
                  }).then((result) => {
                    const res = result.getContent();
                    setUnifiedReports(res.content)
                    return res})
                }
                hideActions={true}
                denseTable
                enablePagination={false}
                renderDialogContent={() => <></>}
                tableColumns={[
                  {
                    header: t("Date"),
                    content: (instance) => <span>{moment(instance?.shortDay).format("DD/MMM")}</span>,
                  },
                  {
                    header: t("Internal Hours"),
                    content: (instance) => <span>{instance.internalHours}</span>,
                  },
                  {
                    header: t("Distribution"),
                    content: (instance) => (
                      <p style={{ textAlign: "left" }}>
                        {Object.entries(instance.hoursByProject)
                          .map(([project, hours]) => `${project} (${hours})`)
                          .join(" / ")}
                      </p>
                    ),
                  },
                ]}
                stylesForRowIfCondition={(instance) => {
                  if (!instance.internalHours) return { backgroundColor: ColorUtils.LIGHT_GRAY };
                  else return undefined;
                }}
              />
            ),
            [selectedUser, monthFilter, yearFilter]
          )}
          {/* Dialog to manage filters  */}
          <FilterDialog title={"Unified Reports"} setIsOpen={setIsOpen} isOpen={isOpen}>
            {renderDialogFilters}
          </FilterDialog>
        </Box>
      }
    />
  );
}

const useStyles = makeStyles((theme) => ({
  desktopFilters: {
    borderRadius: "4px",
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${ColorUtils.LIGHTER_GRAY}`,
    boxSizing: 'inherit',
    boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "row",
    columnGap: "10px",
    "&>div ": {
      margin: "0 !important",
    },
  },
  mobileFilters: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    "&> div:first-child": {
      minWidth: "150px",
      width: "100%",
      marginLeft: "0",
    },
  },
  dialogFilters: {
    padding: "30px 15px",
    rowGap: "10px",
    display: "flex",
    flexDirection: "column",
    "& div": {
      margin: "0 !important",
      width: "100% !important",
    },
  },
  datePicker: {
    "&>div>div": {
      width: "100% !important",
    },
  },
  inputStyles: {
    "& .MuiOutlinedInput-root": {
      paddingBlock: "0.2rem !important",
    },
    width: "15rem",
  },
}),
{ index: 1 }
);
